import { useEffect, useState } from 'react';

/**
 * @hook useIsDesktop
 * @description Is the viewport at Tailwind's `lg` breakpoint or wider?
 *
 *   For state that is only *meaningful* on a desktop layout, where a CSS
 *   `lg:` prefix cannot express it. Collapsing the sidebar is the case that
 *   asked for it: a collapsed rail renders different children (icons, no
 *   labels), not just different classes, so the decision has to be made in
 *   JavaScript — and it was being made without asking the viewport at all,
 *   which put the desktop rail's innards inside the mobile drawer.
 *
 *   Reactive, unlike the bare `window.innerWidth < 1024` reads it replaces:
 *   those answered once at render and never again, so rotating a tablet left
 *   the layout believing whatever was true when it mounted.
 */
export const DESKTOP_MIN_WIDTH = 1024; // Tailwind `lg`

const QUERY = `(min-width: ${DESKTOP_MIN_WIDTH}px)`;

// jsdom (and any non-browser render) has no matchMedia; fall back to the
// width, then to "desktop" so a server render matches the wider layout.
export const readIsDesktop = () => {
  if (typeof window === 'undefined') return true;
  if (typeof window.matchMedia === 'function') return window.matchMedia(QUERY).matches;
  return window.innerWidth >= DESKTOP_MIN_WIDTH;
};

export default function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(readIsDesktop);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const list = window.matchMedia(QUERY);
    const onChange = (event) => setIsDesktop(event.matches);

    // addListener is the Safari < 14 spelling; still worth keeping while the
    // app supports iPads that never took the newer one.
    if (list.addEventListener) list.addEventListener('change', onChange);
    else list.addListener(onChange);

    setIsDesktop(list.matches);

    return () => {
      if (list.removeEventListener) list.removeEventListener('change', onChange);
      else list.removeListener(onChange);
    };
  }, []);

  return isDesktop;
}
